/**
 * 同一ページ内のアンカーリンクを、貼り付いているヘッダーごしに正しく動かす。
 * （2026-09-04 日高さん報告「よくある質問を押しても遷移しない時がある／行きすぎる」）
 *
 * ★ 何が起きていたか ★
 *   メニューの「よくある質問」は `/#aicon-faq`。トップページで押すと、
 *   ブラウザから見ればハッシュだけの移動＝**ページ遷移が起きない**。
 *   ところがメニューは全画面のオーバーレイ（`<details class="menu-drawer-container">`）で
 *   `scroll-lock` が付いているため、開いたまま裏でスクロールしようとしても動かず、
 *   利用者には「押しても何も起きない」ように見える。
 *   他のメニュー項目（/pages/... など）は本当に遷移するので**結果的に**閉じており、
 *   ページ内アンカーだけが取り残されていた。
 *
 *   これは assets/aicon-calendar-bar.js の `openFromEntryPoint()` が
 *   「メニューの『工事可能日を調べる』だけ反応しない」として既に潰した形と同じもの。
 *   あちらはカレンダーバー専用（`[data-aicon-calendar-entry]`）なので、
 *   ページ内アンカー全般はこちらで受ける。
 *
 * ★ 閉じ切ってからスクロールする ★
 *   `<details>` の `toggle` は**非同期に発火する**。`open = false` の直後に
 *   scrollIntoView しても scroll-lock（解除は assets/dialog.js が document の
 *   `toggle` を capture で拾って走る）が残っていて動かない。
 *   要素側で `toggle` を1回だけ待ち、さらに rAF でスタイル適用を待ってから動かす。
 *
 * ★ 行き過ぎの方は CSS で直している ★
 *   遮蔽ぶんの逃げは各アンカー先の `scroll-margin-top`
 *   （例: sections/aicon-faq.liquid の `.aicon-faq`）。
 *   ここで JS が数値を足すと、CSS 側と二重に持つことになり、
 *   工事費ページで一度やった「ヘッダーを二重に数える」失敗を繰り返す。
 *   `scrollIntoView` は scroll-margin を尊重するので、JS は「動かす」だけでよい。
 *
 * ★ 見つからない id は素通しする ★
 *   `/#aicon-faq` は**トップページの要素**を指すので、商品ページなど別ページで
 *   押されたときは preventDefault してはいけない（そのページには id が無い）。
 *   その場合は既定動作＝実際のページ遷移に任せる。
 *
 * ★ 別ページから来たときは何もしない（2026-09-05・一度入れて外した）★
 *   別ページの `/#aicon-faq` は本物のページ遷移で、ブラウザは**解析中の位置**で
 *   スクロール先を決める。「上の遅延画像が後から高さを決めて着地点がずれる」と考えて、
 *   `load` の後に測り直す処理を一度入れたが、**外した**。理由:
 *
 *   ① そのズレはこのテーマでは起きない。theme check の `ImgWidthAndHeight`
 *      （`theme-check:recommended`）が全 img に width/height を強制していて、
 *      場所が先に確保されるため遅延読み込みで高さは動かない。
 *   ② 残る揺れ（カレンダーバーの文言が読み込み後に1行ぶん変わる）は `load` の
 *      **後**に起きるので、`load` 時点の測り直しでは元々つかまえられない。
 *   ③ 一方で副作用があった。listener を張れるのは defer が動き出した後＝
 *      HTML を解析し終えた後なので、**解析中に指で動かしたお客様を検知できず、
 *      load のタイミングで引き戻す**。位置で見分ける回避策も、ブラウザの
 *      スクロールアンカリングが scrollY を動かすため成立しなかった（実測）。
 *
 *   ∴ 効かない対策のために「勝手に画面が動く」危険だけを増やしていた。
 *   本当に必要になったら、判定材料を増やす（head で早めに操作を検知する等）
 *   ところから設計し直すこと。**同じ理由でそのまま戻さないこと。**
 *
 * ★ 別ページから来ると上に着地する本当の原因（日高さん報告 2026-09-07）★
 *   「メニューのよくある質問を押すと、よくある質問ではなくトップの一番上が出る。
 *     キャッシュが残ってるだけか？」
 *   **キャッシュではない。** 画面幅で決まるので「たまに」に見えていた。
 *
 *   assets/scroll-container.js の squeeze レイアウトが原因。
 *   **横幅 990px 以上では、スクロールしているのは document ではなく `.page-wrapper`** で、
 *   `document.scrollingElement` は `overflow: hidden` ＝ **絶対にスクロールしない**。
 *   ところがブラウザの `#...` の着地は document のスクローラを動かす仕組みなので、
 *   **何も起きない ＝ ページ先頭のまま**になる。
 *   990px 未満（スマホ）では document がスクローラなので普通に着地する。
 *   ∴ 「PC・タブレット横向きだと必ず失敗、スマホだと成功」で、
 *     同じ操作なのに成否が変わる＝「たまにバグる」の正体。
 *
 *   もう1つ、同じ squeeze レイアウトでは `history.scrollRestoration = 'manual'` にして
 *   `pageshow` で保存済みの scrollTop に戻す。トップページを一度上の方で見ていると
 *   その位置（＝0 付近）が保存されており、**着地したあとに引き戻す**。
 *   こちらは scroll-container.js 側で「ハッシュがある時は復元しない」ようにした。
 *
 * ★ 2026-09-05 に外した対策とは別物 ★
 *   あちらは「遅延画像で高さが変わり着地点がずれる」という**別の見立て**で、
 *   `load` の後に測り直すものだった。こちらは
 *     ・原因が特定できている（スクローラが document ではない）
 *     ・`load` を待たない。**このスクリプトが走った時点**（body 末尾の defer ＝
 *       解析直後）に1回だけ直す。`load` までの長い間に指で動かされる隙が無い
 *     ・それでも走り出しの瞬間から操作を見張り、動かされていたら何もしない
 *   ので、外した対策の ③（解析中の操作を検知できず引き戻す）には当たらない。
 */
(() => {
  if (window.__aiconAnchorScrollBound) return;
  window.__aiconAnchorScrollBound = true;

  const DRAWER_SELECTOR = 'details.menu-drawer-container[open]';

  /*
   * ★ 走り出した瞬間から「お客様が自分で動かしたか」を見張る ★
   *   下の着地の直しは、これが立っていたら何もしない。
   *   wheel / touchmove / keydown は「動かそうとした意思」で、scroll より確実
   *   （scroll はスクロールアンカリングやこちらの scrollIntoView でも発火する）。
   */
  let userMoved = false;
  const markMoved = () => {
    userMoved = true;
  };
  for (const type of ['wheel', 'touchmove', 'keydown']) {
    window.addEventListener(type, markMoved, { once: true, passive: true });
  }

  /** ハッシュから要素を引く。`#` だけ・数字始まりの id でも落ちないようにする */
  const findTarget = (hash) => {
    const raw = hash.slice(1);
    if (!raw) return null;
    let id = raw;
    try {
      id = decodeURIComponent(raw);
    } catch {
      /* 壊れた %エスケープはそのまま id として扱う */
    }
    return document.getElementById(id) || document.getElementsByName(id)[0] || null;
  };

  /** 開いているメニュードロワーを閉じ、閉じ切ってから run を呼ぶ */
  const closeDrawersThen = (run) => {
    const drawers = document.querySelectorAll(DRAWER_SELECTOR);
    if (drawers.length === 0) {
      run();
      return;
    }
    drawers[0].addEventListener('toggle', () => requestAnimationFrame(run), { once: true });
    for (const drawer of drawers) drawer.open = false;
  };

  document.addEventListener('click', (event) => {
    // 修飾キー・中クリックは「別タブで開く」などの操作。触らない
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;
    /*
     * ★ 専用の持ち主がいるリンクは触らない ★
     *   `[data-aicon-calendar-entry]` は assets/aicon-calendar-bar.js が
     *   document の click で拾い、**ドロワーを閉じ切ってから**バーを開く
     *   （`openFromEntryPoint()`）。こちらが先にドロワーを閉じてしまうと、
     *   あちらは「開いているドロワーが無い」と判断して scroll-lock が
     *   残ったまま動こうとする＝あちらが直したはずの不具合が戻る。
     *   いまは登録順の都合であちらが先に走り `defaultPrevented` で降りているが、
     *   読み込み順に依存させない。
     */
    if (link.closest('[data-aicon-calendar-entry]')) return;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch {
      return;
    }

    if (!url.hash || url.hash === '#') return;
    // 同じページ（オリジン・パス・クエリまで一致）のときだけ引き受ける
    if (url.origin !== window.location.origin) return;
    if (url.pathname !== window.location.pathname) return;
    if (url.search !== window.location.search) return;

    const target = findTarget(url.hash);
    if (!target) return; // 別ページの要素を指している → 既定の遷移に任せる

    event.preventDefault();

    /*
     * 「戻る」で元の位置に帰れるように履歴には残す。
     * ハッシュが同じでも pushState は成功する（location.hash 代入だと何も起きない）。
     *
     * ★ state に null を渡してよい ★
     *   assets/scroll-container.js が history.pushState を差し替えていて、
     *   呼ぶと先に `saveScrollPosition()`（replaceState で**いま居るエントリ**に
     *   scrollTop を書く）が走る。∴「戻る」は復元される。
     *   ここで `history.state` を引き継ぐと、**直前のエントリの scrollTop** が
     *   新しいエントリに付いてしまい、「進む」で戻ったときに見当違いの位置へ
     *   復元される。付けないほうが安全（`scrollTop == null` で何もしない）。
     */
    if (url.hash !== window.location.hash) {
      history.pushState(null, '', url.hash);
    }

    closeDrawersThen(() => {
      target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      // キーボード・支援技術のために、読み上げ位置も移す。
      // 元から tabindex を持たない要素に足した場合は、フォーカスが外れたら片付ける。
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
        target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
      }
      target.focus({ preventScroll: true });
    });
  });

  /*
   * 別ページから `/#aicon-faq` で着いたときの着地をやり直す。
   *
   * ★ 位置を測って分岐しない ★
   *   `scroll-margin-top` のぶん下がって着地するのが正解なので、「上に居るか」を
   *   座標で判定すると閾値の当てっこになる。**常に scrollIntoView を呼ぶ**。
   *   すでに正しく着地しているブラウザ（スマホ幅）では同じ位置なので何も起きない。
   *
   * ★ behavior は instant ★
   *   着地のやり直しなので、動く様子を見せる意味がない。
   *   `.page-wrapper` の `scroll-behavior: smooth` を上書きするためにも明示する。
   *
   *   なお `'instant'` は Safari 17.4 未満 / Chrome 116 未満では WebIDL の列挙外で
   *   TypeError になる。テーマ内に既に同じ書き方が2箇所あり
   *   （sections/main-collection.liquid・assets/scroll-container.js）、
   *   この変更で新しく持ち込んだリスクではない。
   *   ★ ただし iPhone 実機確認のときは古い iOS でも1度見ておくこと（本プロジェクトの規約）。
   *   落ちても try/catch は無いので、必要になったら 'auto' へ寄せる。
   */
  const relandOnHash = () => {
    if (userMoved) return;
    /*
     * 「戻る・進む」では動かさない（2026-09-07 レビュー指摘）。
     * ハッシュは pushState で履歴に残るので、戻ってきたエントリにも付いている。
     * そこで着地をやり直すと、お客様が見ていた位置ではなくアンカー先へ飛ぶ。
     * 位置の復元は assets/scroll-container.js の pageshow が担当する。
     */
    try {
      if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') return;
    } catch {
      /* Navigation Timing が無い環境。やり直す側へ倒す（従来どおり） */
    }
    if (!window.location.hash || window.location.hash === '#') return;
    const target = findTarget(window.location.hash);
    if (!target) return;
    requestAnimationFrame(() => {
      if (userMoved) return;
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
    });
  };

  /*
   * body 末尾の defer なので、ここに来た時点で解析は終わっており対象の要素も居る。
   * 念のため、まだ読み込み中なら DOMContentLoaded まで待つ（読み込み方を変えた場合の保険）。
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', relandOnHash, { once: true });
  } else {
    relandOnHash();
  }

})();
